import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
// Constants
import {
  ADMIN_PAGE_URL,
  BATHROOMS_PAGE_URL,
  BUILDING_WORK_PAGE_URL,
  CARPENTRY_PAGE_URL,
  CONTACT_PAGE_URL,
  COOKIE_TIMER,
  CookiePolicyName,
  GROUNDWORKS_PAGE_URL,
  HOME_PAGE_URL,
  MAINTENANCE_PAGE_URL,
  POLICIES_PAGE_URL,
} from './utils/Constants';
// Components
import LoadingScreen from './components/utils/LoadingScreen';
// Utils
import { AuthenticateAdmin } from './utils/user/AuthenticateUser';
// Normal import for HomePage (no lazy loading)
import HomePage from './pages/home/HomePage';
import CarpentryPage from './pages/carpentry/CarpentryPage';
import BathroomsPage from './pages/bathrooms/BathroomsPage';
import GroundworksPage from './pages/groundworks/GroundworksPage';
import BuildingPage from './pages/building/BuildingPage';
// Lazy-loaded Pages
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const MaintenancePage = lazy(() =>
  import('./pages/maintenance/MaintenancePage')
);
const ContactPage = lazy(() => import('./pages/contact/ContactPage'));
const Error404 = lazy(() => import('./pages/error/Error404'));
const TermAndPoliciesPage = lazy(() =>
  import('./pages/policies/TermAndPoliciesPage')
);

// Components
const CookieConsentModal = lazy(() =>
  import('./components/modals/CookieConsentModal')
);

function App() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAgreedToCookies, setHasAgreedToCookies] = useState(true);

  useEffect(() => {
    const cookie = localStorage.getItem(CookiePolicyName);

    if (cookie) {
      setHasAgreedToCookies(true);
    } else {
      setHasAgreedToCookies(false);
    }
  }, []);

  useEffect(() => {
    if (hasAgreedToCookies) {
      setIsVisible(false);
    }

    const timer = setTimeout(() => {
      if (!hasAgreedToCookies) {
        setIsVisible(true);
      }
    }, COOKIE_TIMER);

    return () => clearTimeout(timer);
  }, [hasAgreedToCookies]);

  return (
    <>
      {isVisible && (
        <Suspense>
          <CookieConsentModal setHasAgreedToCookies={setHasAgreedToCookies} />
        </Suspense>
      )}

      {/* Suspense component wraps the lazy-loaded components */}
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Main page routes */}
          <Route path={HOME_PAGE_URL} index element={<HomePage />} />

          {/* Eager loaded */}
          <Route path={CARPENTRY_PAGE_URL} element={<CarpentryPage />} />
          <Route path={BATHROOMS_PAGE_URL} element={<BathroomsPage />} />
          <Route path={GROUNDWORKS_PAGE_URL} element={<GroundworksPage />} />
          <Route path={BUILDING_WORK_PAGE_URL} element={<BuildingPage />} />
          <Route path={CONTACT_PAGE_URL} element={<ContactPage />} />
         
          {/* Other */}
          <Route path={POLICIES_PAGE_URL} element={<TermAndPoliciesPage />} />
          <Route path={MAINTENANCE_PAGE_URL} element={<MaintenancePage />} />

          {/* Secured routes */}
          <Route
            path={ADMIN_PAGE_URL}
            element={
              <AuthenticateAdmin>
                <AdminPage />
              </AuthenticateAdmin>
            }
          />

          {/* Error routes */}
          <Route path='*' element={<Error404 />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
